/**
 * Represents the Nuxt Route object
 */
export interface NuxtRoute {
  name: string | undefined;
}

/**
 * Removes the localization suffix from a route. Useful for checking which route the user is on in a localized nuxt app.
 * @param {NuxtRoute} route The route the user is currently on
 * @returns {string | undefined} The unlocalized route name
 */
export function unlocalizeRouteName(route: NuxtRoute): string | undefined {
  // nuxt route names will have the name of the route in the following format
  // <route_name>__<locale>
  const localeSplitRoute = route.name?.split('__') ?? [];

  // in normal operation of the app, there will be two parts once the route name is split.
  // if there are more than two parts, one of the developers has given a poor name to the route.
  // this should be reported.
  if (localeSplitRoute.length > 2) {
    console.error(
      'This app does not support route names with double underscores. Rename your route',
      { route }
    );
  }

  // the name of the route without the locale at the end
  const unlocalizedRouteName = localeSplitRoute[0];

  return unlocalizedRouteName;
}

/**
 * Returns whether the route the user is currently on belongs to the locale specified.
 * @param {NuxtRoute} route The route the user is currently on
 * @param {string} locale The locale in question
 */
export function routeBelongsToLocale(
  route: NuxtRoute,
  locale: string
): boolean {
  return route.name?.includes('__' + locale) ?? false;
}

/**
 * Returns the base route name of the route.
 * @param {NuxtRoute} route The route the user is currently on
 * @returns {string} The base route name
 */
export function getBaseRouteName(route: NuxtRoute): string {
  return route.name?.split('___')?.[0] || '';
}

/**
 * Returns the title key of the route.
 * Used to set metadata for the route in the route-meta plugin.
 * These are translation keys that will be translated in the plugin.
 */
export const routeTitles = {
  index: 'home',
  'portfolios-portfolioId': 'portfolio_details',
  portfolios: 'my_portfolios',
  'notifications-center': 'notifications',
  settings: 'settings',
  leagues: 'leagues',
  'leagues-leagueId': 'league_details',
  messages: 'messages',
  search: 'search',
  'academy-index': 'academy',
  onboarding: 'welcome',
  // TODO from Nikola: deduplicate markets and markets-index.
  'markets-index': 'markets',
  'profile-profileId': 'profile',
  markets: 'markets',
  'post-postId': 'post_details',
  academy: 'academy',
  saved: 'saved_posts',
};
